import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, HeartPulse, Activity, ScanLine, TestTube2, Radio } from "lucide-react";
import SEO from "@/components/SEO";
import { ASSETS, CLINIC, buildWhatsAppUrl } from "@/constants/site";
import { TESTIDS } from "@/constants/testIds";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const services = [
  { icon: Radio,     title: "Ressonância Magnética",     img: ASSETS.mri,        desc: "Imagens de alta resolução para diagnóstico neurológico e ortopédico." },
  { icon: ScanLine,  title: "Tomografia Computadorizada", img: ASSETS.tomograph,  desc: "Canon Aquilion multi-cortes para avaliação detalhada em 3D." },
  { icon: Activity,  title: "Ultrassonografia",          img: ASSETS.ultrasound, desc: "Exame indolor, sem radiação, para órgãos abdominais e ecocardio." },
  { icon: HeartPulse,title: "Raio-X Digital",            img: ASSETS.xray,       desc: "Radiografia digital rápida com laudo especializado." },
  { icon: TestTube2, title: "Coleta Laboratorial",       img: ASSETS.bloodDraw,  desc: "Coleta de líquor e materiais para exames laboratoriais complementares." },
];

const differentials = [
  { icon: ShieldCheck, title: "Anestesia Segura",      text: "Médicos veterinários anestesistas dedicados em cada exame que exige sedação." },
  { icon: Sparkles,    title: "Tecnologia de Ponta",   text: "Equipamentos Siemens Magnetom C! e Canon Aquilion — padrão referência." },
  { icon: HeartPulse,  title: "Laudo Especializado",   text: "Radiologistas veterinários com laudos precisos em curto prazo." },
];

export default function Home() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/blog/posts?limit=3").then(r => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Início"
        description="Visiovet: clínica de diagnóstico por imagem veterinária em Belo Horizonte. Ressonância, tomografia, ultrassom e raio-x com tecnologia de ponta."
      />

      {/* HERO */}
      <section data-testid={TESTIDS.homeHero} className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-28 pb-20 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-50 text-brand-700 border border-brand-100 px-3 py-1 text-xs font-medium mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              Belo Horizonte · Barro Preto
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Diagnóstico por imagem <span className="text-brand-500">preciso</span> para o seu pet.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              A Visiovet é um centro de diagnóstico veterinário exclusivo em exames de imagem. Ressonância magnética, tomografia, ultrassom e raio-x com equipe especializada e equipamentos de alta resolução.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                data-testid={TESTIDS.homeHeroCta}
                href={buildWhatsAppUrl("Olá Visiovet! Gostaria de agendar um exame.")}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-brand-500 text-white font-semibold px-7 py-3.5 shadow-lg shadow-brand-500/30 hover:bg-brand-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
              >
                Agendar pelo WhatsApp <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/servicos" className="inline-flex items-center gap-2 text-slate-700 font-semibold hover:text-brand-600 transition-colors">
                Ver todos os exames <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat number="15+" label="anos de experiência" />
              <Stat number="5"   label="modalidades de exame" />
              <Stat number="100%" label="foco em imagem" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] max-w-lg ml-auto">
              <div className="absolute -top-6 -left-6 w-40 h-40 bg-brand-100 rounded-3xl -z-0" />
              <div className="absolute -bottom-6 -right-6 w-56 h-56 bg-brand-50 rounded-3xl -z-0" />
              <img
                src={ASSETS.mri}
                alt="Sala de ressonância magnética veterinária Visiovet"
                className="relative z-10 w-full h-full object-cover rounded-3xl shadow-2xl shadow-slate-900/10"
              />
              <div className="absolute bottom-4 left-4 z-20 bg-white/90 backdrop-blur-md rounded-2xl px-4 py-3 shadow-lg border border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center text-white">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Equipamento</div>
                  <div className="text-sm font-semibold text-slate-900">Siemens Magnetom C!</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-slate-50 section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Nossos exames</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Diagnóstico veterinário completo em um só lugar
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Todos os exames são realizados com equipamentos modernos e acompanhados por radiologistas e anestesistas veterinários especializados.
            </p>
          </div>

          <div data-testid={TESTIDS.homeServicesGrid} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-brand-300 hover:shadow-xl hover:shadow-brand-500/10 hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300"
              >
                {s.img && (
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6">
                  <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIALS */}
      <section className="bg-white section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <img src={ASSETS.facade} alt="Fachada Visiovet" className="rounded-3xl shadow-xl shadow-slate-900/10 aspect-[4/3] w-full object-cover" />
          </div>
          <div>
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Por que Visiovet</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-8">
              Referência em diagnóstico por imagem veterinário em BH
            </h2>
            <div className="space-y-6">
              {differentials.map((d) => (
                <div key={d.title} className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                    <d.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">{d.title}</h3>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{d.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION BANNER */}
      <section className="bg-brand-500 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-white">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Estamos no Barro Preto, em Belo Horizonte
            </h2>
            <p className="text-brand-50 text-lg leading-relaxed">
              {CLINIC.address}
            </p>
            <p className="text-brand-50 mt-2">{CLINIC.hours.weekdays} · {CLINIC.hours.saturday}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={buildWhatsAppUrl("Olá Visiovet!")} target="_blank" rel="noopener noreferrer"
                className="rounded-full bg-white text-brand-700 font-semibold px-6 py-3 hover:bg-brand-50 transition-colors">
                WhatsApp {CLINIC.phoneMobile}
              </a>
              <Link to="/contato" className="rounded-full border border-white/40 text-white font-semibold px-6 py-3 hover:bg-white/10 transition-colors">
                Ver mapa e formulário
              </Link>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/20">
            <iframe
              title="Mapa Visiovet"
              src={CLINIC.mapEmbed}
              className="w-full h-72 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      {posts.length > 0 && (
        <section data-testid={TESTIDS.homeFeaturedBlog} className="bg-slate-50 section-pad">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Blog</div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Conteúdo veterinário atualizado</h2>
              </div>
              <Link to="/blog" className="inline-flex items-center gap-2 text-brand-600 font-semibold hover:text-brand-700">
                Ver todos os artigos <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {posts.slice(0, 3).map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-brand-300 hover:shadow-xl transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1">
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-lg text-slate-900 leading-snug">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-heading text-3xl font-bold text-slate-900">{number}</div>
      <div className="text-xs text-slate-500 mt-1 leading-tight">{label}</div>
    </div>
  );
}
